import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, ShieldCheck, GraduationCap, Users, Sparkles, ArrowLeft, FileText } from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";
import { LESSON_TYPE_LABEL } from "@/constants/options";
import { toast } from "sonner";
import AvailabilityGrid from "@/components/AvailabilityGrid";
import ReviewSection from "@/components/ReviewSection";

export default function TeacherDetail() {
  const { id } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [isFav, setIsFav] = useState(false);
  const { user } = useAuth();
  const [lightbox, setLightbox] = useState(null); // { src, alt }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/teachers/${id}`);
        setTeacher(data.teacher);
        if (user?.role === "family") {
          const favs = await api.get("/favorites");
          setIsFav((favs.data.teacher_ids || []).includes(id));
        }
      } catch {
        toast.error("Öğretmen bulunamadı");
      }
    })();
  }, [id, user]);

  const toggleFav = async () => {
    try {
      if (isFav) {
        await api.delete(`/favorites/${id}`);
        setIsFav(false);
      } else {
        await api.post("/favorites", { teacher_id: id });
        setIsFav(true);
        toast.success("Favorilere eklendi");
      }
    } catch {}
  };

  if (!teacher) return <Layout><div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Yükleniyor...</div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
        <Link to="/ara" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 mb-8" data-testid="back-to-search">
          <ArrowLeft size={14} /> Aramaya dön
        </Link>

        <div className="grid md:grid-cols-3 gap-10 reveal">
          <div className="md:col-span-1">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-neutral-100 border border-neutral-200">
              {teacher.photo_path ? (
                <img
                  src={fileUrl(teacher.photo_path)}
                  alt={teacher.name}
                  onClick={() => setLightbox({ src: fileUrl(teacher.photo_path), alt: teacher.name })}
                  className="w-full h-full object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                  data-testid="teacher-photo-clickable"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-neutral-400"><GraduationCap size={48} /></div>
              )}
              {user?.role === "family" && (
                <button
                  onClick={toggleFav}
                  data-testid="detail-fav-btn"
                  className={`absolute top-3 right-3 h-11 w-11 rounded-full grid place-items-center transition-colors ${
                    isFav ? "bg-[color:var(--star)] text-neutral-900" : "bg-white/90 hover:bg-white text-neutral-700"
                  }`}
                >
                  <Star size={18} fill={isFav ? "currentColor" : "none"} />
                </button>
              )}
            </div>
            {teacher.yks_doc_path && (
              <button
                type="button"
                onClick={() => setLightbox({ src: fileUrl(teacher.yks_doc_path), alt: "YKS Sonuç Belgesi" })}
                className="mt-4 w-full border border-slate-200 rounded-lg overflow-hidden hover:border-[color:var(--brand)] transition-colors group"
                data-testid="yks-doc-link"
              >
                <div className="aspect-[4/3] bg-slate-50">
                  <img
                    src={fileUrl(teacher.yks_doc_path)}
                    alt="YKS Sonuç Belgesi"
                    className="w-full h-full object-cover cursor-zoom-in group-hover:opacity-95 transition-opacity"
                  />
                </div>
                <div className="p-3 flex items-center gap-2 text-sm text-slate-700">
                  <FileText size={16} className="text-[color:var(--brand)]" /> YKS Sonuç Belgesi
                </div>
              </button>
            )}
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              {teacher.verified && (
                <span className="inline-flex items-center gap-1 bg-[color:var(--brand)] text-white rounded-full px-3 py-1 text-xs font-semibold shadow-sm shadow-blue-500/30" data-testid="verified-badge">
                  <ShieldCheck size={13} fill="white" /> Doğrulanmış Öğretmen
                </span>
              )}
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tighter" data-testid="detail-name">{teacher.name}</h1>
            <p className="mt-3 text-neutral-600">{teacher.university} · {teacher.age} yaşında</p>
            {teacher.department && (
              <p className="mt-1 text-sm text-[color:var(--brand)] font-medium" data-testid="detail-department">
                Bölüm: {teacher.department}
              </p>
            )}

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stat icon={<Users size={16} />} label="Aktif öğrenci" value={teacher.active_students} />
              <Stat icon={<Sparkles size={16} />} label="Müsait kontenjan" value={teacher.available_slots} />
              <Stat icon={<GraduationCap size={16} />} label="Branş sayısı" value={teacher.subjects?.length || 0} />
            </div>

            <Section title="Branşlar">
              <TagList tags={teacher.subjects} />
            </Section>

            <Section title="Sınıf seviyeleri">
              <TagList tags={teacher.class_levels} />
            </Section>

            <Section title="Ders türü">
              <TagList tags={(teacher.lesson_types || []).map((v) => LESSON_TYPE_LABEL[v] || v)} />
            </Section>

            <Section title="Hakkında">
              <p className="text-neutral-700 leading-relaxed whitespace-pre-line">{teacher.intro}</p>
            </Section>
          </div>
        </div>

        {/* Availability (read-only for everyone viewing) */}
        <div className="mt-16 border-t border-slate-200 pt-12">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-display text-2xl font-semibold tracking-tight">Müsaitlik Takvimi</h3>
            <span className="text-xs text-slate-500">Öğretmenin müsait olduğu saatler (bilgi amaçlıdır)</span>
          </div>
          <AvailabilityGrid teacherId={id} editable={false} />
        </div>

        {/* Reviews */}
        <ReviewSection teacherId={id} />

        {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
      </div>
    </Layout>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border border-neutral-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium flex items-center gap-1.5">{icon} {label}</div>
      <div className="mt-2 font-display text-2xl font-semibold">{value}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="mt-8">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-medium mb-3">{title}</div>
      {children}
    </div>
  );
}
function TagList({ tags }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(tags || []).map((t) => (
        <span key={t} className="text-sm px-3 py-1.5 rounded-full border border-neutral-200 bg-neutral-50">{t}</span>
      ))}
    </div>
  );
}
